import { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { buildRoute } from 'routes';
import Link from '@mui/material/Link';

import http from 'app/services/httpService';
import Notify from 'utils/Notify';
import apiEndpoints from 'routes/apiEndpoints';
import DateUtils from 'utils/DateUtils';
import FormCheckbox from 'core/forms/FormCheckbox';
import TableContainer from 'core/table/TableContainer';
import TanstackReactTable from 'core/table/TanstackReactTable';

import constants from 'app/config/constants';
import Dimensions from 'app/config/Dimensions';
import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';
import { getFlashPaymentWebhookLogs } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSaga';
import { getFlashPaymentWebhookLogBriefDetailsInit } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSlice';
import FlashPaymentWebhookLogsActions from './FlashPaymentWebhookLogsActions';
import FlashPaymentWebhookLogPagination from './FlashPaymentWebhookLogPagination';

const ContentWrapper = styled(Box)({
  maxWidth: '220px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export default function FlashPaymentWebhookLogs({ filterByCustomer, customerId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [columnVisibility, setColumnVisibility] = useState({});
  const [resolvingTransactionId, setResolvingTransactionId] = useState(null);

  const handleOpenTransaction = useCallback(
    async (orderId) => {
      if (!orderId || orderId === 'N/A') return;
      setResolvingTransactionId(orderId);
      try {
        const response = await http.get(
          `${apiEndpoints.getTransactions}?id=${encodeURIComponent(orderId)}&page_number=1&page_size=1`,
        );
        const transaction = response?.data?.data?.[0];
        if (!transaction?.id) {
          Notify.error('No transaction found with the provided Order ID.');
          return;
        }
        navigate(buildRoute('transactions.show', transaction.id));
      } catch {
        Notify.error('Unable to open transaction details.');
      } finally {
        setResolvingTransactionId(null);
      }
    },
    [navigate],
  );

  const logsState = useSelector((state) => state.flashPaymentWebhookLogs.index);

  const { data, params, isLoading } = logsState;

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_CHECKBOX_SELECT_CHECKBOX_ID,
        header: ({ table }) => (
          <FormCheckbox
            forTableHead={true}
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div>
            <FormCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },
      {
        header: 'Transaction ID',
        accessorFn: (row) => (row.transactionId ? row.transactionId : 'N/A'),
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value || value === 'N/A') return 'N/A';
          return (
            <Link
              component="button"
              type="button"
              underline="hover"
              disabled={resolvingTransactionId === value}
              onClick={() => handleOpenTransaction(value)}
            >
              {value}
            </Link>
          );
        },
      },
      {
        header: 'Flash Payment ID',
        accessorFn: (row) => (row.flashPaymentId ? row.flashPaymentId : 'N/A'),
      },
      {
        header: 'Customer ID',
        accessorFn: (row) => (row.customerId ? row.customerId : 'N/A'),
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value || value === 'N/A') return 'N/A';
          return (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => navigate(buildRoute('customers.show', value, 'tab=customer-details'))}
            >
              {value}
            </Link>
          );
        },
      },
      {
        header: 'Amount',
        accessorFn: (row) => (row.amount || row.amount === 0 ? row.amount : 'N/A'),
      },
      {
        header: 'Flash Pay User ID',
        accessorFn: (row) => (row.flashPayUserId ? row.flashPayUserId : 'N/A'),
      },
      {
        header: 'External Reference No',
        accessorFn: (row) => (row.externalReferenceNo ? row.externalReferenceNo : 'N/A'),
      },
      {
        header: 'Transaction Status',
        accessorFn: (row) => (row.transactionStatus ? row.transactionStatus : 'N/A'),
      },
      {
        header: 'Sender Name in Flash',
        accessorFn: (row) => (row.senderCompanyName ? row.senderCompanyName : 'N/A'),
      },
      {
        header: 'Sender Bank Name',
        accessorFn: (row) => (row.senderBankName ? row.senderBankName : 'N/A'),
      },
      {
        header: 'Sender Bank Country',
        accessorFn: (row) => (row.senderBankCountry ? row.senderBankCountry : 'N/A'),
      },
      {
        header: 'Beneficiary Account Name',
        accessorFn: (row) => (row.recipientAccountName ? row.recipientAccountName : 'N/A'),
      },
      {
        header: 'Status Message',
        accessorFn: (row) => (row.statusMessage ? row.statusMessage : 'N/A'),
        cell: ({ getValue }) => <ContentWrapper>{getValue()}</ContentWrapper>,
      },
      {
        header: 'Response Message',
        accessorFn: (row) => (row.responseMessage ? row.responseMessage : 'N/A'),
        cell: ({ getValue }) => <ContentWrapper>{getValue()}</ContentWrapper>,
      },
      {
        header: 'Master Account Log',
        accessorFn: (row) => (row.isMasterAccountLog ? 'Yes' : 'No'),
      },
      {
        header: 'Created At',
        accessorFn: (row) => DateUtils.isoToString(row.createdAt),
      },
      {
        header: 'Cleared At',
        accessorFn: (row) => DateUtils.isoToString(row.clearedAt),
      },
      {
        header: 'Created By',
        accessorFn: (row) => (row.createdBy ? row.createdBy : 'N/A'),
      },
      {
        header: 'Updated Date',
        accessorFn: (row) => DateUtils.isoToString(row.updatedTs),
      },
      {
        header: 'Updated By',
        accessorFn: (row) => (row.updatedBy ? row.updatedBy : 'N/A'),
      },
      {
        id: constants.TABLE_ACTION_COLUMN_ID,
        header: 'Action',
        accessorFn: (row) => row.id,
        cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <HasPermission permission={permissions.VIEW_FLASH_WEBHOOK_TRANSACTION_LOG}>
              <IconButton
                size={Dimensions.table.iconButton.size}
                onClick={() => {
                  dispatch(
                    getFlashPaymentWebhookLogBriefDetailsInit({
                      showBriefDetailModal: true,
                      data: row.original,
                    }),
                  );
                }}
              >
                <AspectRatioIcon fontSize={Dimensions.table.iconButton.iconSize} />
              </IconButton>
            </HasPermission>
          </Box>
        ),
      },
    ],
    [handleOpenTransaction, resolvingTransactionId, navigate],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    dispatch(
      getFlashPaymentWebhookLogs({
        ...params,
        ...(filterByCustomer ? { CustomerId: customerId } : {}),
      }),
    );
  }, [JSON.stringify(params), filterByCustomer, customerId]);

  return (
    <TableContainer>
      <FlashPaymentWebhookLogsActions
        table={table}
        filterByCustomer={filterByCustomer}
        defaultFilterQueries={filterByCustomer ? { CustomerId: customerId } : {}}
      />
      <TanstackReactTable
        table={table}
        isLoading={isLoading}
        data={data}
        columnsCount={
          columns.length - Object.entries(columnVisibility ?? {}).filter(([, v]) => !v).length
        }
      />
      <FlashPaymentWebhookLogPagination />
    </TableContainer>
  );
}

FlashPaymentWebhookLogs.propTypes = {
  filterByCustomer: PropTypes.bool,
  customerId: PropTypes.string,
};

FlashPaymentWebhookLogs.defaultProps = {
  filterByCustomer: false,
  customerId: null,
};
